import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import BranchSubNav from "@/components/branches/BranchSubNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Info, ExternalLink, Loader2, Save } from "lucide-react";
import { branchesApi, type Branch } from "@/lib/api/branches";

interface BranchPolicies {
  loanDurationDays?: number | null;
  maxRenewals?: number | null;
  fineRatePerDay?: number | null;
  allowInterBranchReturns?: boolean;
  allowHoldsFromOtherBranches?: boolean;
}

const BranchSettingsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isTenantAdmin } = useAccessControl();
  const [branch, setBranch] = useState<Branch | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingFrom, setSavingFrom] = useState<"header" | "footer" | null>(null);
  const saving = savingFrom !== null;
  const [policies, setPolicies] = useState<BranchPolicies>({
    loanDurationDays: null,
    maxRenewals: null,
    fineRatePerDay: null,
    allowInterBranchReturns: false,
    allowHoldsFromOtherBranches: false,
  });

  useEffect(() => {
    if (id) loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await branchesApi.getById(id!);
      setBranch(data);
      const settings = data.settings as any;
      if (settings?.policies) {
        setPolicies({ ...policies, ...settings.policies });
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load branch.");
      navigate("/setup/branches");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (from: "header" | "footer") => {
    try {
      setSavingFrom(from);
      const settings = { ...((branch?.settings as any) || {}), policies };
      await branchesApi.update(id!, { settings });
      toast.success("Branch settings saved.");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to save settings.");
    } finally {
      setSavingFrom(null);
    }
  };

  const handleNumericChange = (field: keyof BranchPolicies, value: string) => {
    const n = value === "" ? null : Number(value);
    setPolicies((p) => ({ ...p, [field]: n }));
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <BranchSubNav branch={branch} />

      <PageContainer width="form">
        <PageHeader
          title="Branch Settings"
          description={`Override tenant-wide policies for ${branch?.name ?? "this branch"}. Leave blank to inherit global defaults.`}
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Branches", href: "/setup/branches" },
            { label: branch?.name ?? "Branch", href: id ? `/setup/branches/${id}` : undefined },
            { label: "Settings" },
          ]}
          showSearch={false}
          titleSize="md"
          breadcrumbAlign="start"
          actions={
            isTenantAdmin ? (
              <Button onClick={() => handleSave("header")} disabled={saving} className="gap-2">
                {savingFrom === "header" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Settings
              </Button>
            ) : undefined
          }
        />

        {/* Info banner */}
        <div className="flex gap-3 p-4 rounded-lg border border-primary/20 bg-primary/5 text-sm text-primary">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p>
              These settings override per-branch circulation rules. Leave a field{" "}
              <strong>empty</strong> to inherit the global default.
            </p>
            <p className="text-primary">
              <strong>Where are global defaults set?</strong> Loan duration, max renewals, and fine
              rates are defined per <em>Patron Type</em> — not as a single global number — because
              different patron categories (e.g. Undergraduate vs. Staff vs. External) have different
              borrowing rules. A branch override applies on top of those per-type rules.
            </p>
            <button
              onClick={() => navigate("/setup/patron-types")}
              className="inline-flex items-center gap-1 font-medium underline underline-offset-2 hover:text-primary/90 transition-colors"
            >
              View Patron Types &amp; their default limits
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Circulation overrides */}
        <Card className="p-6 space-y-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Circulation Overrides
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <Label htmlFor="loanDuration" className="text-sm font-medium">
                Loan Duration Override (days)
              </Label>
              <p className="text-xs text-gray-400 mb-1.5">
                Blank = use each patron type's own default
              </p>
              <Input
                id="loanDuration"
                type="number"
                min={1}
                max={365}
                placeholder="e.g. 14"
                value={policies.loanDurationDays ?? ""}
                onChange={(e) => handleNumericChange("loanDurationDays", e.target.value)}
                disabled={!isTenantAdmin}
              />
            </div>

            <div>
              <Label htmlFor="maxRenewals" className="text-sm font-medium">
                Max Renewals Override
              </Label>
              <p className="text-xs text-gray-400 mb-1.5">
                Blank = use each patron type's own default
              </p>
              <Input
                id="maxRenewals"
                type="number"
                min={0}
                max={20}
                placeholder="e.g. 2"
                value={policies.maxRenewals ?? ""}
                onChange={(e) => handleNumericChange("maxRenewals", e.target.value)}
                disabled={!isTenantAdmin}
              />
            </div>

            <div>
              <Label htmlFor="fineRate" className="text-sm font-medium">
                Fine Rate Per Day Override (₦)
              </Label>
              <p className="text-xs text-gray-400 mb-1.5">
                Blank = use each patron type's own default
              </p>
              <Input
                id="fineRate"
                type="number"
                min={0}
                step={0.01}
                placeholder="e.g. 50.00"
                value={policies.fineRatePerDay ?? ""}
                onChange={(e) => handleNumericChange("fineRatePerDay", e.target.value)}
                disabled={!isTenantAdmin}
              />
            </div>
          </div>
        </Card>

        {/* Inter-branch features */}
        <Card className="p-6 space-y-5">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Inter-Branch Features
          </h2>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="interBranchReturns"
                checked={policies.allowInterBranchReturns ?? false}
                onCheckedChange={(v) =>
                  setPolicies((p) => ({ ...p, allowInterBranchReturns: Boolean(v) }))
                }
                disabled={!isTenantAdmin}
                className="mt-0.5"
              />
              <div>
                <Label htmlFor="interBranchReturns" className="text-sm font-medium cursor-pointer">
                  Allow inter-branch returns
                </Label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Patrons can return items checked out at another branch to this one.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="holdsFromOthers"
                checked={policies.allowHoldsFromOtherBranches ?? false}
                onCheckedChange={(v) =>
                  setPolicies((p) => ({ ...p, allowHoldsFromOtherBranches: Boolean(v) }))
                }
                disabled={!isTenantAdmin}
                className="mt-0.5"
              />
              <div>
                <Label htmlFor="holdsFromOthers" className="text-sm font-medium cursor-pointer">
                  Accept hold requests from other branches
                </Label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Items at this branch can be placed on hold by patrons registered at other
                  branches.
                </p>
              </div>
            </div>
          </div>
        </Card>

        {isTenantAdmin && (
          <div className="flex justify-end">
            <Button onClick={() => handleSave("footer")} disabled={saving} className="gap-2">
              {savingFrom === "footer" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save All Settings
            </Button>
          </div>
        )}
      </PageContainer>
    </AppLayout>
  );
};

export default BranchSettingsPage;
